import { BASE_PATH } from 'src/store/config';
import { getValidAccessToken } from 'src/store/oauth-browser';
import type { StoredTranslation, TargetLang } from './translation-types';

/**
 * Shared, organization-wide storage for content translations.
 *
 * Translations are persisted through the organization attachment endpoint
 * (PUT/GET/DELETE /organizations/{id}/attachments/{documentName}), which stores
 * a named blob to a versioned, S3-only bucket. We store one JSON blob per
 * (source entity, language, source version): the document name embeds a hash of
 * the Norwegian source, so a changed original yields a new key and a fresh
 * translation, and the stored blob always matches the source it was made from.
 *
 * The store is scoped to the organization (not the user), so every member reads
 * the same translation. Writing (PUT) requires write access — read-only users
 * cannot persist their own translations, which is accepted for now. S3
 * versioning provides the change log.
 */
const SCHEMA_VERSION = 1;

function documentName(sourceType: string, sourceUuid: string, lang: TargetLang, sourceHash: string): string {
  return `translation_${sourceType}_${sourceUuid}_${lang}_${sourceHash}.json`;
}

function attachmentUrl(organizationId: string, name: string): string {
  return `${BASE_PATH}/organizations/${organizationId}/attachments/${encodeURIComponent(name)}`;
}

/** Build a complete record, stamping schema version and timestamp. */
export function newStoredTranslation(partial: Omit<StoredTranslation, 'v' | 'updatedAt'>): StoredTranslation {
  return { ...partial, v: SCHEMA_VERSION, updatedAt: new Date().toISOString() };
}

/**
 * Load the shared translation for a given entity, language and source version,
 * or null if none has been stored for this exact source version yet.
 */
export async function loadStoredTranslation(
  organizationId: string,
  sourceType: string,
  sourceUuid: string,
  lang: TargetLang,
  sourceHash: string,
): Promise<StoredTranslation | null> {
  try {
    const token = await getValidAccessToken();
    const response = await fetch(
      attachmentUrl(organizationId, documentName(sourceType, sourceUuid, lang, sourceHash)),
      {
        method: 'GET',
        headers: { Authorization: 'Bearer ' + token },
      },
    );
    if (!response.ok) {
      // 404 (or similar) = nothing stored for this source version yet.
      return null;
    }
    const data = (await response.json()) as Partial<StoredTranslation>;
    return data && typeof data.fields === 'object' && data.fields !== null ? (data as StoredTranslation) : null;
  } catch {
    return null;
  }
}

/** Persist a translation to the shared, org-wide store. Requires write access. */
export async function saveStoredTranslation(organizationId: string, translation: StoredTranslation): Promise<void> {
  const token = await getValidAccessToken();
  const name = documentName(translation.sourceType, translation.sourceUuid, translation.lang, translation.sourceHash);
  const response = await fetch(attachmentUrl(organizationId, name), {
    method: 'PUT',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/octet-stream',
    },
    body: JSON.stringify(translation),
  });
  if (!response.ok) {
    throw new Error(`Failed to save translation (${response.status})`);
  }
}
